import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

const ClientAssociations = ({ clientId }) => {
  const [associations, setAssociations] = useState([]);
  const [associationTypes, setAssociationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [formData, setFormData] = useState({
    source_entity_type: 'client',
    source_entity_id: clientId,
    target_entity_type: 'client',
    target_entity_id: '',
    association_type_id: '',
    association_strength: 'primary',
    start_date: '',
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    if (clientId) {
      fetchAssociations();
      fetchAssociationTypes();
    }
  }, [clientId]);

  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch associations where this client is either source or target
      const response = await axios.get(`/api/associations/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allAssociations = response.data || [];
      
      // Filter out self-references but keep all valid associations
      const validAssociations = allAssociations.filter(assoc => 
        assoc.source_entity_id !== assoc.target_entity_id
      );
      
      setAssociations(validAssociations);
      
    } catch (error) {
      console.error('Error fetching associations:', error);
      setAssociations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociationTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/associations/types?active_only=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssociationTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching association types:', error);
      setAssociationTypes([]);
    }
  };

  const searchEntities = async (entityType, searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/associations/search/${entityType}?q=${encodeURIComponent(searchTerm)}&exclude=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching entities:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.target_entity_id) {
      setError('Please search for and select a related entity');
      return;
    }
    
    if (!formData.association_type_id) {
      setError('Please select a relationship type');
      return;
    }
    
    console.log('Submitting form data:', formData);

    try {
      const token = localStorage.getItem('token');
      
      if (editingAssociation) {
        // Update existing association
        await axios.put(`/api/associations/${editingAssociation.id}`, {
          association_strength: formData.association_strength,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          notes: formData.notes
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new association
        const postData = {
          ...formData,
          source_entity_id: parseInt(formData.source_entity_id),
          target_entity_id: parseInt(formData.target_entity_id),
          association_type_id: parseInt(formData.association_type_id)
        };
        console.log('POST data being sent:', postData);
        
        await axios.post('/api/associations', postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      await fetchAssociations();
      resetForm();
      setError('');
    } catch (error) {
      console.error('Error saving association:', error);
      setError(error.response?.data?.error || 'Failed to save association');
    }
  };

  const handleEdit = (association) => {
    setEditingAssociation(association);
    setFormData({
      source_entity_type: association.source_entity_type,
      source_entity_id: association.source_entity_id,
      target_entity_type: association.target_entity_type,
      target_entity_id: association.target_entity_id,
      association_type_id: association.association_type_id,
      association_strength: association.association_strength || 'primary',
      start_date: association.start_date || '',
      end_date: association.end_date || '',
      notes: association.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this association?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/associations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAssociations();
      setError('');
    } catch (error) {
      console.error('Error deleting association:', error);
      setError(error.response?.data?.error || 'Failed to delete association');
    }
  };

  const resetForm = () => {
    setFormData({
      source_entity_type: 'client',
      source_entity_id: clientId,
      target_entity_type: 'client',
      target_entity_id: '',
      association_type_id: '',
      association_strength: 'primary',
      start_date: '',
      end_date: '',
      notes: ''
    });
    setEditingAssociation(null);
    setShowForm(false);
    setSearchResults([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Trigger search when entity type changes
    if (name === 'target_entity_type') {
      setSearchResults([]);
    }
  };

  const selectEntity = (entity) => {
    setFormData(prev => ({
      ...prev,
      target_entity_id: entity.id
    }));
    setSearchResults([]);
  };

  const getRelationshipDirection = (association) => {
    const associationType = associationTypes.find(type => type.id === association.association_type_id);
    if (!associationType) return association.association_type_name || 'Unknown';
    
    // If this client is the source, show the display_label
    if (association.source_entity_id === parseInt(clientId)) {
      return associationType.display_label || associationType.name;
    }
    // If this client is the target, show the reverse_label
    return associationType.reverse_label || associationType.display_label || associationType.name;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add Association Button (only show when editing) */}
      {!showForm && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Relationship
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-medium text-gray-900">
              {editingAssociation ? 'Edit Relationship' : 'Add Relationship'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingAssociation && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Related Entity Type *
                    </label>
                    <select
                      name="target_entity_type"
                      value={formData.target_entity_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    >
                      <option value="client">Client</option>
                      <option value="vendor">Vendor</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship Type *
                    </label>
                    <select
                      name="association_type_id"
                      value={formData.association_type_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    >
                      <option value="">Select relationship type...</option>
                      {associationTypes
                        .filter(type => 
                          type.source_entity_type === formData.source_entity_type &&
                          type.target_entity_type === formData.target_entity_type
                        )
                        .map(type => (
                          <option key={type.id} value={type.id}>
                            {type.display_label} ({type.name})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search for {formData.target_entity_type} *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={`Search ${formData.target_entity_type}s...`}
                      onChange={(e) => searchEntities(formData.target_entity_type, e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  </div>
                  {formData.target_entity_id && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <span className="text-sm text-green-800">
                        Selected: {formData.target_entity_type} #{formData.target_entity_id}
                      </span>
                    </div>
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Search Results:</h5>
                    <div className="space-y-2">
                      {searchResults.map(entity => (
                        <div key={entity.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded border border-gray-100">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {entity.name} {entity.dba && `(${entity.dba})`}
                            </div>
                            {entity.city && entity.state && (
                              <div className="text-xs text-gray-500">
                                {entity.city}, {entity.state}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => selectEntity(entity)}
                            className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                          >
                            Select
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strength
                </label>
                <select
                  name="association_strength"
                  value={formData.association_strength}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="weak">Weak</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Additional notes about this relationship"
                rows="2"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {editingAssociation ? 'Update' : 'Create'} Relationship
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Associations List */}
      {associations.length === 0 ? (
        <div className="text-center py-6">
          <LinkIcon className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No relationships found.</p>
          <button 
            onClick={() => setShowForm(true)}
            className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add First Relationship
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {associations.map(association => (
            <div key={association.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getRelationshipDirection(association)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      association.association_strength === 'primary' ? 'bg-green-100 text-green-800' :
                      association.association_strength === 'secondary' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {association.association_strength}
                    </span>
                  </div>
                  
                  <div className="mt-2">
                    <div className="text-sm font-medium text-gray-900">
                      {association.source_entity_id === parseInt(clientId) 
                        ? (association.target_entity?.name || `${association.target_entity_type} #${association.target_entity_id}`)
                        : (association.source_entity?.name || `${association.source_entity_type} #${association.source_entity_id}`)
                      }
                    </div>
                    {association.source_entity_id === parseInt(clientId) 
                      ? (association.target_entity?.city && association.target_entity?.state && (
                          <div className="text-xs text-gray-500">
                            {association.target_entity.city}, {association.target_entity.state}
                          </div>
                        ))
                      : (association.source_entity?.city && association.source_entity?.state && (
                          <div className="text-xs text-gray-500">
                            {association.source_entity.city}, {association.source_entity.state}
                          </div>
                        ))
                    }
                  </div>

                  {(association.start_date || association.end_date) && (
                    <div className="mt-1 text-xs text-gray-500">
                      {association.start_date && `Start: ${new Date(association.start_date).toLocaleDateString()}`}
                      {association.start_date && association.end_date && ' • '}
                      {association.end_date && `End: ${new Date(association.end_date).toLocaleDateString()}`}
                    </div>
                  )}

                  {association.notes && (
                    <div className="mt-1 text-sm text-gray-600">
                      {association.notes}
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(association)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(association.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientAssociations; 